import { useParams, Link } from "react-router-dom";
import { YouTubeEmbed, DownloadCard, ChartBlock, Newsletter } from "../components";
import { POSTS } from "../data/content";
import GearSection from "../components/GearCard";

function renderContent(content) {
  // Simple markdown-lite renderer
  const lines = content.trim().split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line.startsWith("## ")) {
      elements.push(<h2 key={i}>{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      elements.push(<h3 key={i}>{line.slice(4)}</h3>);
    } else if (line.startsWith("| ")) {
      // Table
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const headers = tableLines[0].split("|").filter(Boolean).map(h => h.trim());
      const rows = tableLines.slice(2).map(r => r.split("|").filter(Boolean).map(c => c.trim()));
      elements.push(
        <table key={`table-${i}`}>
          <thead><tr>{headers.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((row, j) => <tr key={j}>{row.map((cell, k) => <td key={k}>{cell}</td>)}</tr>)}</tbody>
        </table>
      );
      continue;
    } else if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(<ul key={`ul-${i}`}>{items.map((item, j) => <li key={j} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />)}</ul>);
      continue;
    } else if (line.match(/^\d+\. /)) {
      const items = [];
      while (i < lines.length && lines[i].trim().match(/^\d+\. /)) {
        items.push(lines[i].trim().replace(/^\d+\. /, ""));
        i++;
      }
      elements.push(<ol key={`ol-${i}`}>{items.map((item, j) => <li key={j}>{item}</li>)}</ol>);
      continue;
    } else if (line !== "") {
      const html = line
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(
          /\[([^\]]+)\]\(\/blog\/([^)]+)\)/g,
          '<a href="/blog/$2" style="color:var(--ochre);text-decoration:underline;">$1</a>'
        )
        .replace(
          /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:var(--ochre);text-decoration:underline;">$1</a>'
        );
      elements.push(<p key={i} dangerouslySetInnerHTML={{ __html: html }} />);
    }

    i++;
  }

  return elements;
}

export default function Post() {
  const { slug } = useParams();
  const post = POSTS.find(p => p.slug === slug);
  const related = POSTS.filter(p => p.slug !== slug && p.category === post?.category).slice(0, 3);
  const allOthers = POSTS.filter(p => p.slug !== slug).slice(0, 3);

  if (!post) {
    return (
      <div className="page" style={{ padding: "6rem 5rem", textAlign: "center" }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", marginBottom: "1rem" }}>Post not found</h1>
        <Link to="/blog" className="back-link">← Back to blog</Link>
      </div>
    );
  }

  return (
    <div className="page">
      {/* POST HEADER BANNER */}
      <div style={{ background: "var(--bg-dark)", padding: "3rem 5rem 2.5rem" }}>
        <Link to="/blog" className="back-link" style={{ color: "rgba(255,255,255,0.4)" }}>← All stories</Link>
        <div className="post-category" style={{ marginTop: "1rem" }}>{post.category}</div>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.8rem,3.5vw,3rem)", fontWeight: 900, color: "white", lineHeight: 1.1, maxWidth: "720px", marginBottom: "1.25rem" }}>
          {post.title}
        </h1>
        <div className="post-meta" style={{ color: "rgba(255,255,255,0.35)" }}>
          <span>{post.date}</span>
          <span className="meta-dot" />
          <span>{post.readTime}</span>
        </div>
        {post.tags && (
          <div className="post-tags" style={{ marginTop: "1rem" }}>
            {post.tags.map(t => <span key={t} className="post-tag" style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>{t}</span>)}
          </div>
        )}
      </div>

      {/* BODY */}
      <div className="post-layout">
        <article className="post-main">
          {/* YouTube embed */}
          {post.youtubeId && (
            <>
              <div style={{ marginBottom: "0.75rem" }}>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.64rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-mute)" }}>
                  Watch the video version ↓
                </span>
              </div>
              <YouTubeEmbed videoId={post.youtubeId} />
            </>
          )}

          {/* Chart if applicable */}
          {post.chartId && <ChartBlock chartId={post.chartId} />}

          {/* Article content */}
          <div className="post-content">
            {renderContent(post.content)}
          </div>

          {/* Downloads */}
          {post.downloads?.length > 0 && (
            <div className="download-section">
              <div className="download-section-title">Free downloads for this post</div>
              <div className="download-cards">
                {post.downloads.map((d, i) => (
                  <DownloadCard key={i} icon={d.icon} label={d.label} file={d.file} />
                ))}
              </div>
            </div>
          )}
          {post.gearIds?.length > 0 && (
  <>
    <div className="download-section-title" style={{ marginTop: "3rem" }}>
      Gear I Used
    </div>
    <GearSection gearIds={post.gearIds} />
  </>
)}
        </article>

        {/* SIDEBAR */}
        <aside className="post-sidebar">
          <div className="sidebar-widget">
            <div className="sidebar-label">Related posts</div>
            {(related.length > 0 ? related : allOthers).map(p => (
              <Link to={`/blog/${p.slug}`} className="related-card" key={p.id}>
                <div className="related-category">{p.category}</div>
                <div className="related-title">{p.title}</div>
              </Link>
            ))}
          </div>

          <div className="sidebar-widget">
            <div className="sidebar-label">Free resources</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <Link to="/guides" style={{ display: "block", padding: "0.9rem 1rem", background: "var(--ochre)", color: "white", fontSize: "0.8rem", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center", transition: "opacity 0.2s" }}>
                Browse all guides ↓
              </Link>
            </div>
          </div>

          <div className="sidebar-widget">
            <div className="sidebar-label">YouTube channel</div>
            <a href="https://www.youtube.com/@saketdownunder" target="_blank" rel="noopener noreferrer" style={{ display: "block", padding: "0.9rem 1rem", background: "#FF0000", color: "white", fontSize: "0.8rem", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center" }}>
              Subscribe →
            </a>
          </div>
        </aside>
      </div>

      <Newsletter />
    </div>
  );
}
